<template>
	<div>
		<loading-modal v-if="isLoading" loading-text="Loading Report Data..." />

		<div v-if="reportData">
			<div class="report-toolbar">
				<v-layout row>
					<v-flex>
						<v-layout row wrap>
							<v-flex xl3 lg4 md4 sm6 xs12 class="pr-3">
								<v-text-field v-debounce:300ms="updateSearch" ref="focusAfterTagSelector" append-icon="search" label="Search Employees" autofocus="" background-color="#fff"></v-text-field>
							</v-flex>
							<v-flex xl3 lg4 md4 sm6 xs12 class="pr-3">
								<tag-selector
									:options="tagList"
									:selectedOptions="selectedTags"
									:isMatchAll="matchAllTags"
									placeholder="Enter Tag"
									@on-selected-tags-change="updateSelectedTags"
									@on-match-all-tags-change="updateMatchAllTags"
									@on-leave="onLeaveTagSelector"
								></tag-selector>
							</v-flex>
							<v-flex xl3 lg4 md4 sm6 xs12>
								<v-select v-model="filterDepartment" :items="departmentOptions" item-text="name" item-value="id" label="Department"></v-select>
							</v-flex>
						</v-layout>
					</v-flex>

					<v-flex shrink>
						<v-tooltip bottom>
							<template v-slot:activator="{ on }">
								<v-btn icon v-on="on" @click="print">
									<v-icon>print</v-icon>
								</v-btn>
							</template>
							<span>Print Report</span>
						</v-tooltip>
					</v-flex>
				</v-layout>
			</div>

			<h1 class="printOnly">Employee Compliance Report</h1>
			<h3 v-if="filterDepartment !== -1" class="printOnly mb-1">{{ filterDepartmentName }}</h3>
			<v-subheader class="printOnly text-xs-center">{{ new Date() | formatDateTime }}</v-subheader>
			<div v-if="totalEmployeeCount === 0" class="text-xs-left mt-3">
				<v-alert :value="true" color="info" outline icon="info"> No report data to display </v-alert>
			</div>
			<v-container v-else grid-list-xl pa-0 fluid>
				<v-layout row wrap>
					<v-flex xl3 lg4 md6 sm12>
						<v-card class="mb-2 print-no-borders">
							<v-toolbar flat dense>
								<v-toolbar-title class="subheading">Employee Compliance</v-toolbar-title>
								<v-spacer></v-spacer>
							</v-toolbar>
							<v-card-text class="text-xs-left">
								<table>
									<tr>
										<td style="width: 1px">
											<v-progress-circular :rotate="360" :size="100" :width="15" :value="compliantEmployeesPercent" :color="progressColor"> {{ compliantEmployeesPercent }}% </v-progress-circular>
										</td>
										<td class="pl-4">
											<div class="text-xs-center">
												<div style="font-size: 32px; font-weight: bold">{{ uncompliantEmployeeCount }}</div>
												<span>{{ uncompliantCountDescription }}</span>
											</div>
										</td>
									</tr>
								</table>
							</v-card-text>
						</v-card>
					</v-flex>
				</v-layout>

				<v-layout row wrap>
					<v-flex v-if="uncompliantEmployeeCount > 0" md6>
						<v-card class="print-no-borders">
							<v-toolbar flat dense class="print" :dark="uncompliantEmployeeCount > 0" :color="uncompliantEmployeeCount > 0 ? 'error' : ''">
								<v-icon v-if="uncompliantEmployeeCount > 0">mdi-alert-circle-outline</v-icon>
								<v-icon v-else color="success">mdi-check-circle-outline</v-icon>
								<v-toolbar-title class="subheading">Employees with Pending Compliance Items</v-toolbar-title>
								<v-spacer></v-spacer>
							</v-toolbar>
							<v-expansion-panel v-if="uncompliantEmployeeCount > 0" v-model="expansionPanel" expand class="uncompliant-employees">
								<v-expansion-panel-content v-for="(employee, i) in uncompliantEmployees" :key="i">
									<template v-slot:header>
										<v-list dense>
											<v-list-tile>
												<v-list-tile-action>
													<v-avatar size="30px">
														<img v-if="employee !== null" :src="getAvatar(employee.id)" class="avatar-img" />
													</v-avatar>
												</v-list-tile-action>
												<v-list-tile-content>
													<v-list-tile-title>
														<router-link class="employee-name-link" :to="{ name: 'employee', params: { id: employee.id } }">{{ employee.fullName }}</router-link>
													</v-list-tile-title>
													<v-list-tile-sub-title v-if="employee.expiredCertifications.length > 0">
														<strong>{{ employee.expiredCertifications.length }}</strong> expired certification{{ employee.expiredCertifications.length > 1 ? 's' : '' }}
													</v-list-tile-sub-title>

													<v-list-tile-sub-title v-if="employee.unacknowledgedPolicies.length > 0">
														<strong>{{ employee.unacknowledgedPolicies.length }}</strong> policies require acknowledgment
													</v-list-tile-sub-title>

													<v-list-tile-sub-title v-if="employee.unacknowledgedPD">
														<strong>1</strong> unacknowledged PD: <strong>{{ employee.unacknowledgedPD.positionTitle }}</strong>
													</v-list-tile-sub-title>
												</v-list-tile-content>
											</v-list-tile>
										</v-list>
									</template>

									<div style="background-color: #fbefef" class="pa-4 noPrint">
										<div v-if="employee.expiredCertifications.length > 0" class="mb-4">
											<h4 class="mb-2">Expired Certifications</h4>
											<div class="certs-container">
												<v-sheet v-for="certification in employee.expiredCertifications" :key="certification.certificationId" color="white" class="expired-sheet cert-sheet">
													<v-icon color="error">'mdi-alert-circle'</v-icon>
													<div>
														<h4>{{ certification.certificationName }}</h4>
														<div class="caption">
															Expired
															<span class="red--text text--darken-1">{{ moment(certification.expiryDate).fromNow() }}</span>
														</div>
													</div>
												</v-sheet>
											</div>
										</div>

										<h4 class="mb-2" v-if="employee.unacknowledgedPolicies.length > 0">Unacknowledged Policies</h4>
										<unacknowledged-hr-documents-list
											v-if="employee.unacknowledgedPolicies.length > 0"
											class="mb-3"
											:hr-documents="employee.unacknowledgedPolicies"
											:is-logged-in-employee="false"
											hide-last-updated
											title="Unacknowledged Policies"
										>
											<template slot="heading">
												<p class="caption px-3 pt-3 pb-1"><v-icon class="mr-2" color="warning" small>mdi-alert-circle-outline</v-icon>The following policies require acknowledgement from the employee:</p>
											</template>
										</unacknowledged-hr-documents-list>

										<h4 class="mb-2" v-if="employee.unacknowledgedPD">Unacknowledged Position Description</h4>
										<unacknowledged-position-description-list
											v-if="employee.unacknowledgedPD"
											:unacknowledged-positions="employee.unacknowledgedPD != null ? [employee.unacknowledgedPD] : []"
											:employeeId="employee.id"
											:is-logged-in-employee="false"
										>
											<template slot="heading">
												<p class="caption px-3 pt-3 pb-1"><v-icon class="mr-2" color="warning" small>mdi-alert-circle-outline</v-icon>The following position description requires acknowledgement from the employee:</p>
											</template>
										</unacknowledged-position-description-list>
									</div>
								</v-expansion-panel-content>
							</v-expansion-panel>
							<div v-else>
								<v-card-text class="text-xs-left"> All employees are compliant </v-card-text>
							</div>
						</v-card>
					</v-flex>
					<v-flex md6>
						<v-card class="print-no-borders">
							<v-toolbar flat dense class="printOnly">
								<v-icon color="success">mdi-check-circle-outline</v-icon>
								<v-toolbar-title class="subheading">Compliant Employees ({{ `${compliantEmployeeCount}/${totalEmployeeCount}` }})</v-toolbar-title>
								<v-spacer></v-spacer>
							</v-toolbar>
							<v-toolbar flat dense dark color="success" class="noPrint">
								<v-toolbar-title class="subheading">Compliant Employees ({{ `${compliantEmployeeCount}/${totalEmployeeCount}` }})</v-toolbar-title>
								<v-spacer></v-spacer>
							</v-toolbar>
							<v-data-table class="elevation-1" :items="compliantEmployees" hide-actions hide-headers>
								<template v-slot:items="props">
									<tr>
										<td class="text-xs-left">
											<v-avatar size="30px">
												<img v-if="props.item !== null" :src="getAvatar(props.item.id)" class="avatar-img" />
											</v-avatar>
											<router-link class="ml-3" :to="{ name: 'employee', params: { id: props.item.id } }">{{ props.item.fullName }}</router-link>
										</td>
									</tr>
								</template>
							</v-data-table>
						</v-card>
					</v-flex>
				</v-layout>
			</v-container>
		</div>
	</div>
</template>

<script>
	import { mapGetters } from 'vuex';
	import avatarMixin from '@/mixins/avatarMixin';
	import LoadingModal from '@/components/LoadingModal';
	import TagSelector from '@/components/tags/TagSelector';
	import UnacknowledgedHrDocumentsList from '@/components/compliance/UnacknowledgedHrDocumentsList';
	import UnacknowledgedPositionDescriptionList from '@/components/compliance/UnacknowledgedPositionDescriptionList';
	import moment from 'moment';

	export default {
		data() {
			return {
				reportData: null,
				isLoading: false,
				expansionPanel: [],
				filterDepartment: -1,
				search: null,
				selectedTags: [],
				matchAllTags: false
			};
		},
		components: {
			LoadingModal,
			TagSelector,
			UnacknowledgedHrDocumentsList,
			UnacknowledgedPositionDescriptionList
		},
		async created() {
			this.isLoading = true;
			await this.getReportData();
			this.isLoading = false;
		},
		watch: {
			uncompliantEmployees() {
				if (!this.uncompliantEmployees) this.expansionPanel = [];

				this.expansionPanel = this.uncompliantEmployees.map(x => false);
			}
		},
		mixins: [avatarMixin],
		computed: {
			...mapGetters({
				loggedInTenant: 'loggedInTenant',
				loggedInEmployee: 'loggedInEmployee',
				departmentOptions: 'departments/departmentOptions',
				tagList: 'tags/employeeTagList'
			}),
			employees() {
				return !this.reportData ? [] : this.reportData.employees;
			},
			filteredEmployees() {
				return this.employees.filter(x => this.filteredEmployeeIds.includes(x.id));
			},
			filteredEmployeeIds() {
				const filterOptions = {
					search: this.search,
					tags: this.selectedTags,
					matchAllTags: this.matchAllTags,
					departmentId: this.filterDepartment !== -1 ? this.filterDepartment : null
				};
				return this.$_getFilteredEmployeeIds(this.loggedInEmployee, filterOptions);
			},
			uncompliantEmployees() {
				return this.filteredEmployees.filter(x => !x.isCompliant);
			},
			uncompliantCountDescription() {
				if (this.uncompliantEmployeeCount === 1) {
					return 'Employee has pending compliance items';
				} else {
					return 'Employees have pending compliance items';
				}
			},
			compliantEmployees() {
				return this.filteredEmployees.filter(x => x.isCompliant);
			},
			compliantEmployeeCount() {
				return this.compliantEmployees.length;
			},
			uncompliantEmployeeCount() {
				return this.uncompliantEmployees.length;
			},
			totalEmployeeCount() {
				return this.filteredEmployees.length;
			},
			compliantEmployeesPercent() {
				return Math.round((this.compliantEmployeeCount / this.totalEmployeeCount) * 100);
			},
			progressColor() {
				if (this.compliantEmployeesPercent <= 20) {
					return 'info';
				} else if (this.compliantEmployeesPercent < 100) {
					return 'info';
				} else {
					return 'success';
				}
			},
			filterDepartmentName() {
				if (this.filterDepartment === -1) return null;

				return this.departmentOptions.find(x => x.id === this.filterDepartment).name;
			}
		},
		methods: {
			moment,
			async getReportData() {
				try {
					const response = await this.$store.dispatch('reports/get', 'ComplianceReport');
					this.reportData = response.data;
				} catch (e) {}
			},
			print() {
				window.print();
			},
			updateSelectedTags(newSelectedTags) {
				this.selectedTags = newSelectedTags;
			},
			updateMatchAllTags(newMatchAll) {
				this.matchAllTags = newMatchAll;
			},
			onLeaveTagSelector() {
				this.$refs.focusAfterTagSelector.focus();
			},
			updateSearch(val) {
				this.search = val;
			}
		}
	};
</script>
<style scoped>
	.info-text {
		color: rgba(0, 0, 0, 0.54);
		font-size: 12px !important;
	}
	.uncompliant-employees >>> .v-expansion-panel__container--active,
	.uncompliant-employees >>> .v-expansion-panel__container--active .theme--light.v-list {
		background-color: #fbefef;
	}
	.uncompliant-employees >>> .v-expansion-panel__container--active .employee-name-link {
		font-weight: bold;
	}

	.uncompliant-employees >>> .v-list__tile {
		height: auto !important;
		align-items: flex-start;
	}

	.certs-container {
		display: flex;
		flex-wrap: wrap;
		gap: 1rem;
	}

	.cert-sheet {
		display: flex;
		box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12);
		padding: 1.5rem 1rem;
		gap: 1rem;
		min-width: 200px;
	}

	.expired-sheet {
		background-color: #ffeceb !important;
		border-left: 4px solid #ff5252 !important;
	}
</style>
